/* @jsx h */
import { defineComponent, ref, h, watch, computed, PropType } from "vue";
import { RightSheet, useLocalization } from "@lemmonhq/uikit";
import type { Event, EventSchedule, EventTicket } from "@lemmonhq/types";
import { AssetDetails } from "@app/vue/components/AssetDetails";
import type { RestaurantAsset } from "@app/data/api/uploads";
import { useAuth } from "@app/vue/providers/index";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

type EventFormData = Omit<Event, "id"> & { id?: string };

const ASSET_TYPE = "event_images";

// datetime-local inputs are timezone-naive ("YYYY-MM-DDTHH:mm"). We interpret
// them in the restaurant's timezone and persist UTC, so event times render
// correctly regardless of the manager's or customer's browser timezone.
const DATETIME_LOCAL_FORMAT = "YYYY-MM-DDTHH:mm";

export interface EventFormProps {
  event: Event | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (event: Event) => void;
}

export const EventForm = defineComponent<EventFormProps>({
  name: "EventForm",
  props: ["event", "isOpen", "onClose", "onSave"],
  setup(props) {
    const { t } = useLocalization();
    const auth = useAuth();

    const restaurantTz = computed(
      () => auth.user.value?.restaurant?.timezone || "UTC",
    );

    // Stored UTC ISO -> naive datetime-local string in the restaurant timezone.
    const utcToLocalInput = (value?: string) =>
      value
        ? dayjs(value).tz(restaurantTz.value).format(DATETIME_LOCAL_FORMAT)
        : "";

    // Naive datetime-local string (restaurant timezone) -> UTC ISO for storage.
    const localInputToUtc = (value?: string) =>
      value ? dayjs.tz(value, restaurantTz.value).toISOString() : "";

    // Convert a schedule's start_at/end_at between UTC storage and the naive
    // local strings the datetime-local inputs expect.
    const convertScheduleTimes = (
      schedule: EventSchedule,
      convert: (value?: string) => string,
    ): EventSchedule => ({
      ...schedule,
      start_at: convert(schedule.start_at),
      end_at: schedule.end_at ? convert(schedule.end_at) : schedule.end_at,
    });

    const formData = ref<EventFormData>({
      restaurant_id: 0,
      title: "",
      subtitle: "",
      description: "",
      long_description: "",
      image: "",
      date: "",
      time: "",
      schedule: {
        type: "one_off",
        start_at: "",
        end_at: "",
      } as EventSchedule,
      price_from: undefined,
      ticket_types: [],
      tags: [],
      need_to_know: [],
    });

    const scheduleType = ref<"one_off" | "recurring">("one_off");
    const newTag = ref("");
    const newNeedToKnow = ref("");
    const showImageUpload = ref(false);

    const resetForm = () => {
      formData.value = {
        restaurant_id: 0,
        title: "",
        subtitle: "",
        description: "",
        long_description: "",
        image: "",
        date: "",
        time: "",
        schedule: {
          type: "one_off",
          start_at: "",
          end_at: "",
        } as EventSchedule,
        price_from: undefined,
        ticket_types: [],
        tags: [],
        need_to_know: [],
      };
      scheduleType.value = "one_off";
      newTag.value = "";
      newNeedToKnow.value = "";
    };

    // Initialize form data when event changes
    watch(
      () => props.event,
      (event) => {
        if (event) {
          formData.value = {
            ...event,
            schedule: event.schedule
              ? convertScheduleTimes(event.schedule, utcToLocalInput)
              : event.schedule,
          };
          scheduleType.value = event.schedule?.type || "one_off";
        } else {
          resetForm();
        }
      },
      { immediate: true },
    );

    const handleScheduleTypeChange = (type: "one_off" | "recurring") => {
      scheduleType.value = type;
      if (type === "one_off") {
        formData.value.schedule = {
          type: "one_off",
          start_at: "",
          end_at: "",
        };
      } else {
        formData.value.schedule = {
          type: "recurring",
          rrule: "",
          start_at: "",
          end_at: "",
          excluded_dates: [],
        };
      }
    };

    const handleImageUpload = (asset: RestaurantAsset) => {
      if (asset.url) {
        formData.value.image = asset.url;
      }
      showImageUpload.value = false;
    };

    const addTag = () => {
      if (newTag.value.trim()) {
        formData.value.tags.push(newTag.value.trim());
        newTag.value = "";
      }
    };

    const removeTag = (index: number) => {
      formData.value.tags.splice(index, 1);
    };

    const addNeedToKnow = () => {
      if (newNeedToKnow.value.trim()) {
        if (!formData.value.need_to_know) formData.value.need_to_know = [];
        formData.value.need_to_know.push(newNeedToKnow.value.trim());
        newNeedToKnow.value = "";
      }
    };

    const removeNeedToKnow = (index: number) => {
      if (!formData.value.need_to_know) formData.value.need_to_know = [];
      formData.value.need_to_know.splice(index, 1);
    };

    const addTicketType = () => {
      const newTicket: EventTicket = {
        id: crypto.randomUUID(),
        name: "",
        price: 0,
        description: "",
        includes: [],
      };
      if (formData.value.ticket_types) {
        formData.value.ticket_types.push(newTicket);
      } else {
        formData.value.ticket_types = [newTicket];
      }
    };

    const removeTicketType = (index: number) => {
      if (formData.value.ticket_types) {
        formData.value.ticket_types.splice(index, 1);
      }
    };

    const addTicketInclude = (ticketIndex: number) => {
      const includeText = prompt("Enter what's included:");
      if (includeText && formData.value.ticket_types) {
        formData.value.ticket_types[ticketIndex].includes.push(includeText);
      }
    };

    const removeTicketInclude = (ticketIndex: number, includeIndex: number) => {
      if (formData.value.ticket_types) {
        formData.value.ticket_types[ticketIndex].includes.splice(
          includeIndex,
          1,
        );
      }
    };

    const handleSubmit = () => {
      const event: Event = {
        ...formData.value,
        id: formData.value.id || `evt-${Date.now()}`,
        schedule: formData.value.schedule
          ? convertScheduleTimes(formData.value.schedule, localInputToUtc)
          : formData.value.schedule,
      } as Event;
      props.onSave(event);
    };

    return () => (
      <RightSheet
        isOpen={props.isOpen}
        onClose={props.onClose}
        title={
          props.event
            ? t("events.form.edit_event")
            : t("events.form.create_event")
        }
        width="800px"
      >
        <div class="p-6">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
            class="space-y-6"
          >
            {/* Basic Information */}
            <div class="space-y-4">
              <h4 class="text-lg font-semibold text-accent flex items-center gap-2">
                <span class="material-symbols-outlined">info</span>
                {t("events.form.basic_information")}
              </h4>

              <div class="grid grid-cols-1 gap-4">
                <div>
                  <label class="block text-sm font-medium  mb-2">
                    {t("events.form.event_title_label")} *
                  </label>
                  <input
                    type="text"
                    v-model={formData.value.title}
                    class="input input-bordered w-full"
                    placeholder={t("events.form.event_title_placeholder")}
                    required
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium  mb-2">
                    {t("events.form.subtitle_label")}
                  </label>
                  <input
                    type="text"
                    v-model={formData.value.subtitle}
                    class="input input-bordered w-full"
                    placeholder={t("events.form.subtitle_placeholder")}
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium  mb-2">
                    {t("events.form.short_description_label")} *
                  </label>
                  <textarea
                    v-model={formData.value.description}
                    class="textarea textarea-bordered w-full h-20"
                    placeholder={t("events.form.short_description_placeholder")}
                    required
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium  mb-2">
                    {t("events.form.long_description_label")}
                  </label>
                  <textarea
                    v-model={formData.value.long_description}
                    class="textarea textarea-bordered w-full h-32"
                    placeholder={t("events.form.long_description_placeholder")}
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium  mb-2">
                    {t("events.form.price_from_label")}
                  </label>
                  <input
                    type="number"
                    v-model={formData.value.price_from}
                    class="input input-bordered w-full"
                    placeholder="0"
                    min="0"
                    step="0.01"
                  />
                  <p class="text-xs  mt-1">
                    {t("events.form.price_from_hint")}
                  </p>
                </div>
              </div>
            </div>

            {/* Event Image */}
            <div class="space-y-4">
              <h4 class="text-lg font-semibold text-accent flex items-center gap-2">
                <span class="material-symbols-outlined">image</span>
                {t("events.form.event_image")}
              </h4>

              {formData.value.image && (
                <div class="relative">
                  <img
                    src={formData.value.image}
                    alt="Event"
                    class="w-full h-48 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    class="btn btn-sm btn-circle btn-error absolute top-2 right-2"
                    onClick={() => (formData.value.image = "")}
                  >
                    <span class="material-symbols-outlined text-sm">close</span>
                  </button>
                </div>
              )}

              <button
                type="button"
                class="btn btn-outline w-full"
                onClick={() => (showImageUpload.value = true)}
              >
                <span class="material-symbols-outlined">upload</span>
                {formData.value.image
                  ? t("events.form.change_image")
                  : t("events.form.upload_image")}
              </button>
            </div>

            {/* Schedule */}
            <div class="space-y-4">
              <h4 class="text-lg font-semibold text-accent flex items-center gap-2">
                <span class="material-symbols-outlined">schedule</span>
                {t("events.form.schedule")}
              </h4>

              <div class="flex gap-4">
                <label class="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="scheduleType"
                    class="radio radio-primary"
                    checked={scheduleType.value === "one_off"}
                    onChange={() => handleScheduleTypeChange("one_off")}
                  />
                  <span class="">{t("events.form.one_time_event")}</span>
                </label>
                <label class="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="scheduleType"
                    class="radio radio-primary"
                    checked={scheduleType.value === "recurring"}
                    onChange={() => handleScheduleTypeChange("recurring")}
                  />
                  <span class="">{t("events.form.recurring_event")}</span>
                </label>
              </div>

              {scheduleType.value === "one_off" ? (
                <div class="grid grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-medium  mb-2">
                      {t("events.form.start_date_time_label")} *
                    </label>
                    <input
                      type="datetime-local"
                      value={
                        (
                          formData.value.schedule as Extract<
                            EventSchedule,
                            { type: "one_off" }
                          >
                        ).start_at
                      }
                      onInput={(e) => {
                        const target = e.target as HTMLInputElement;
                        (
                          formData.value.schedule as Extract<
                            EventSchedule,
                            { type: "one_off" }
                          >
                        ).start_at = target.value;
                      }}
                      class="input input-bordered w-full"
                      required
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium  mb-2">
                      {t("events.form.end_date_time_label")}
                    </label>
                    <input
                      type="datetime-local"
                      value={
                        (
                          formData.value.schedule as Extract<
                            EventSchedule,
                            { type: "one_off" }
                          >
                        ).end_at
                      }
                      onInput={(e) => {
                        const target = e.target as HTMLInputElement;
                        (
                          formData.value.schedule as Extract<
                            EventSchedule,
                            { type: "one_off" }
                          >
                        ).end_at = target.value;
                      }}
                      class="input input-bordered w-full"
                    />
                  </div>
                </div>
              ) : (
                <div class="space-y-4">
                  <div>
                    <label class="block text-sm font-medium  mb-2">
                      {t("events.form.rrule_string_label")} *
                    </label>
                    <input
                      type="text"
                      value={
                        (
                          formData.value.schedule as Extract<
                            EventSchedule,
                            { type: "recurring" }
                          >
                        ).rrule
                      }
                      onInput={(e) => {
                        const target = e.target as HTMLInputElement;
                        (
                          formData.value.schedule as Extract<
                            EventSchedule,
                            { type: "recurring" }
                          >
                        ).rrule = target.value;
                      }}
                      class="input input-bordered w-full font-mono text-xs"
                      placeholder="FREQ=WEEKLY;BYDAY=FR;BYHOUR=19;BYMINUTE=0"
                      required
                    />
                    <p class="text-xs  mt-1">{t("events.form.rrule_hint")}</p>
                  </div>
                  <div class="grid grid-cols-2 gap-4">
                    <div>
                      <label class="block text-sm font-medium  mb-2">
                        {t("events.form.start_date_label")} *
                      </label>
                      <input
                        type="datetime-local"
                        value={
                          (
                            formData.value.schedule as Extract<
                              EventSchedule,
                              { type: "recurring" }
                            >
                          ).start_at
                        }
                        onInput={(e) => {
                          const target = e.target as HTMLInputElement;
                          (
                            formData.value.schedule as Extract<
                              EventSchedule,
                              { type: "recurring" }
                            >
                          ).start_at = target.value;
                        }}
                        class="input input-bordered w-full"
                        required
                      />
                    </div>
                    <div>
                      <label class="block text-sm font-medium  mb-2">
                        {t("events.form.end_date_label")}
                      </label>
                      <input
                        type="datetime-local"
                        value={
                          (
                            formData.value.schedule as Extract<
                              EventSchedule,
                              { type: "recurring" }
                            >
                          ).end_at
                        }
                        onInput={(e) => {
                          const target = e.target as HTMLInputElement;
                          (
                            formData.value.schedule as Extract<
                              EventSchedule,
                              { type: "recurring" }
                            >
                          ).end_at = target.value;
                        }}
                        class="input input-bordered w-full"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Tags */}
            <div class="space-y-4">
              <h4 class="text-lg font-semibold text-accent flex items-center gap-2">
                <span class="material-symbols-outlined">label</span>
                {t("events.form.tags")}
              </h4>

              <div class="flex flex-wrap gap-2 mb-2">
                {formData.value.tags.map((tag, index) => (
                  <div key={index} class="badge badge-lg gap-2">
                    {tag}
                    <button
                      type="button"
                      class="btn btn-ghost btn-xs btn-circle"
                      onClick={() => removeTag(index)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>

              <div class="flex gap-2">
                <input
                  type="text"
                  v-model={newTag.value}
                  class="input input-bordered flex-1"
                  placeholder={t("events.form.add_tag_placeholder")}
                  onKeypress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addTag();
                    }
                  }}
                />
                <button type="button" class="btn btn-outline" onClick={addTag}>
                  <span class="material-symbols-outlined">add</span>
                </button>
              </div>
            </div>

            {/* Need to Know */}
            <div class="space-y-4">
              <h4 class="text-lg font-semibold text-accent flex items-center gap-2">
                <span class="material-symbols-outlined">info</span>
                {t("events.form.important_information")}
              </h4>

              <div class="space-y-2 mb-2">
                {(formData.value.need_to_know || []).map((item, index) => (
                  <div
                    key={index}
                    class="flex items-center gap-2 p-2 bg-base-200 rounded"
                  >
                    <span class="flex-1 ">{item}</span>
                    <button
                      type="button"
                      class="btn btn-ghost btn-xs btn-circle"
                      onClick={() => removeNeedToKnow(index)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>

              <div class="flex gap-2">
                <input
                  type="text"
                  v-model={newNeedToKnow.value}
                  class="input input-bordered flex-1"
                  placeholder={t("events.form.add_info_placeholder")}
                  onKeypress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addNeedToKnow();
                    }
                  }}
                />
                <button
                  type="button"
                  class="btn btn-outline"
                  onClick={addNeedToKnow}
                >
                  <span class="material-symbols-outlined">add</span>
                </button>
              </div>
            </div>

            {/* Ticket Types */}
            <div class="space-y-4">
              <div class="flex justify-between items-center">
                <h4 class="text-lg font-semibold text-accent flex items-center gap-2">
                  <span class="material-symbols-outlined">
                    confirmation_number
                  </span>
                  {t("events.form.ticket_types")}
                </h4>
                <button
                  type="button"
                  class="btn btn-sm btn-primary"
                  onClick={addTicketType}
                >
                  <span class="material-symbols-outlined">add</span>
                  {t("events.form.add_ticket")}
                </button>
              </div>

              {!formData.value.ticket_types ||
              formData.value.ticket_types.length === 0 ? (
                <div class="text-center p-6 bg-base-200 rounded-lg">
                  <p class=" text-sm">{t("events.form.no_tickets_message")}</p>
                </div>
              ) : (
                <div class="space-y-4">
                  {formData.value.ticket_types?.map((ticket, ticketIndex) => (
                    <div
                      key={ticketIndex}
                      class="p-4 bg-base-200 rounded-lg space-y-3"
                    >
                      <div class="flex justify-between items-start">
                        <h5 class="font-semibold text-accent">
                          {t("events.form.ticket_label")} {ticketIndex + 1}
                        </h5>
                        <button
                          type="button"
                          class="btn btn-ghost btn-xs btn-circle"
                          onClick={() => removeTicketType(ticketIndex)}
                        >
                          ×
                        </button>
                      </div>

                      <div class="grid grid-cols-2 gap-3">
                        <div>
                          <label class="block text-xs font-medium  mb-1">
                            {t("events.form.ticket_name_label")} *
                          </label>
                          <input
                            type="text"
                            value={
                              formData.value.ticket_types?.[ticketIndex]
                                ?.name || ""
                            }
                            onInput={(e) => {
                              const target = e.target as HTMLInputElement;
                              if (formData.value.ticket_types?.[ticketIndex]) {
                                formData.value.ticket_types[ticketIndex].name =
                                  target.value;
                              }
                            }}
                            class="input input-bordered input-sm w-full"
                            placeholder={t(
                              "events.form.ticket_name_placeholder",
                            )}
                            required
                          />
                        </div>

                        <div>
                          <label class="block text-xs font-medium  mb-1">
                            {t("events.form.ticket_price_label")} *
                          </label>
                          <input
                            type="number"
                            value={
                              formData.value.ticket_types?.[ticketIndex]
                                ?.price || 0
                            }
                            onInput={(e) => {
                              const target = e.target as HTMLInputElement;
                              if (formData.value.ticket_types?.[ticketIndex]) {
                                formData.value.ticket_types[ticketIndex].price =
                                  parseFloat(target.value);
                              }
                            }}
                            class="input input-bordered input-sm w-full"
                            placeholder="50"
                            min="0"
                            step="0.01"
                            required
                          />
                        </div>

                        <div class="col-span-2">
                          <label class="block text-xs font-medium  mb-1">
                            {t("events.form.ticket_description_label")}
                          </label>
                          <textarea
                            value={
                              formData.value.ticket_types?.[ticketIndex]
                                ?.description || ""
                            }
                            onInput={(e) => {
                              const target = e.target as HTMLTextAreaElement;
                              if (formData.value.ticket_types?.[ticketIndex]) {
                                formData.value.ticket_types[
                                  ticketIndex
                                ].description = target.value;
                              }
                            }}
                            class="textarea textarea-bordered textarea-sm w-full"
                            placeholder={t(
                              "events.form.ticket_description_placeholder",
                            )}
                            rows="2"
                          />
                        </div>
                      </div>

                      <div>
                        <label class="block text-xs font-medium  mb-2">
                          {t("events.form.whats_included")}
                        </label>
                        <div class="space-y-1 mb-2">
                          {ticket.includes.map((include, includeIndex) => (
                            <div
                              key={includeIndex}
                              class="flex items-center gap-2 text-sm"
                            >
                              <span class="material-symbols-outlined text-xs text-success">
                                check_circle
                              </span>
                              <span class="flex-1 ">{include}</span>
                              <button
                                type="button"
                                class="btn btn-ghost btn-xs"
                                onClick={() =>
                                  removeTicketInclude(ticketIndex, includeIndex)
                                }
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                        <button
                          type="button"
                          class="btn btn-xs btn-outline"
                          onClick={() => addTicketInclude(ticketIndex)}
                        >
                          <span class="material-symbols-outlined text-xs">
                            add
                          </span>
                          {t("events.form.add_item")}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Form Actions */}
            <div class="flex gap-3 justify-end pt-4 border-t  sticky bottom-0 bg-base-100 -mx-6 px-6 -mb-6 pb-6">
              <button
                type="button"
                class="btn btn-ghost"
                onClick={props.onClose}
              >
                {t("events.form.cancel")}
              </button>
              <button type="submit" class="btn btn-primary">
                <span class="material-symbols-outlined">save</span>
                {props.event
                  ? t("events.form.update_event")
                  : t("events.form.create_event")}
              </button>
            </div>
          </form>
        </div>

        {/* Image Upload Modal */}
        {showImageUpload.value && (
          <AssetDetails
            mode="add"
            assetType={ASSET_TYPE}
            isOpen={showImageUpload.value}
            onClose={() => (showImageUpload.value = false)}
            onSave={handleImageUpload}
          />
        )}
      </RightSheet>
    );
  },
});
